import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ArrowLeft, Users, Calendar, MapPin, Clock, Check, ShoppingCart,
  Newspaper, BookOpen, FileText, Globe, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mediaTypeConfig = {
  magazine: { icon: BookOpen, color: 'bg-purple-500', label: 'Magazine' },
  newspaper: { icon: Newspaper, color: 'bg-blue-500', label: 'Newspaper' },
  journal: { icon: FileText, color: 'bg-green-500', label: 'Journal' },
};

const frequencyLabels = {
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

const positionLabels = {
  front_cover: 'Front Cover',
  back_cover: 'Back Cover',
  inside_front: 'Inside Front Cover',
  inside_back: 'Inside Back Cover',
  center_spread: 'Center Spread',
  inside_page: 'Inside Page',
  premium_position: 'Premium Position',
};

const sizeLabels = {
  full_page: 'Full Page',
  half_page: 'Half Page',
  quarter_page: 'Quarter Page',
  double_spread: 'Double Spread',
  strip_ad: 'Strip Ad',
  custom: 'Custom Size',
};

const formatCirculation = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count;
};

const PrintMediaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMediaDetails();
  }, [id]);

  const fetchMediaDetails = async () => {
    try {
      const response = await axios.get(`${API}/print-media/${id}`);
      setMedia(response.data);
      if (response.data.packages && response.data.packages.length > 0) {
        setSelectedPackage(response.data.packages[0]);
      }
    } catch (error) {
      console.error('Error fetching print media:', error);
      toast.error('Failed to load print media details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (!selectedPackage) {
      toast.error('Please select an ad placement');
      return;
    }
    navigate('/checkout', { 
      state: { 
        itemType: 'print_media',
        printMedia: media, 
        package: selectedPackage 
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
          <Printer className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="font-playfair text-2xl font-bold mb-2">Print Media Not Found</h1>
          <p className="text-[#737373] mb-6">The print media you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/marketplace?type=print_media')}
            className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-lg hover:bg-[#B8960F]"
          >
            Browse Print Media
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const typeConfig = mediaTypeConfig[media.media_type] || mediaTypeConfig.magazine;
  const TypeIcon = typeConfig.icon;

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="print-media-detail">
      <Helmet>
        <title>{media.name} - Print Advertising | BusinessMint PR</title>
        <meta name="description" content={`Advertise in ${media.name} with ${formatCirculation(media.circulation)} circulation. ${media.description || ''}`} />
      </Helmet>
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/marketplace?type=print_media')}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1a1a1a] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-manrope text-sm">Back to Print Media</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Media Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-8">
              <div className="flex items-start gap-6">
                {/* Logo */}
                <div className={`w-24 h-24 rounded-xl ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                  {media.logo ? (
                    <img src={media.logo} alt={media.name} className="w-24 h-24 rounded-xl object-contain bg-white p-2" />
                  ) : (
                    <TypeIcon className="w-12 h-12 text-white" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      {frequencyLabels[media.frequency] || media.frequency}
                    </span>
                  </div>
                  <h1 className="font-playfair text-3xl font-bold text-[#1a1a1a] mb-2">{media.name}</h1>
                  <div className="flex items-center gap-2 text-[#737373]">
                    <Globe className="w-4 h-4" />
                    <span className="font-manrope">{media.language || 'English'}</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mt-8 pt-6 border-t border-[#e5e5e5]">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#fee2e2] rounded-lg">
                    <Users className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-bold text-[#1a1a1a]">{formatCirculation(media.circulation)}</p>
                    <p className="text-xs text-[#737373] uppercase tracking-widest">Circulation</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#dbeafe] rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-bold text-[#1a1a1a]">{media.lead_time_days || 15}</p>
                    <p className="text-xs text-[#737373] uppercase tracking-widest">Days Lead Time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {media.description && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">About</h2>
                <p className="font-manrope text-[#737373] leading-relaxed">{media.description}</p>
              </div>
            )}

            {/* Distribution Regions */}
            {media.distribution_regions && media.distribution_regions.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Distribution Regions</h2>
                <div className="flex flex-wrap gap-2">
                  {media.distribution_regions.map((region, idx) => (
                    <span key={idx} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-[#1a1a1a] rounded-full text-sm font-manrope">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Target Audience */}
            {media.target_audience && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Target Audience</h2>
                <p className="font-manrope text-[#737373]">{media.target_audience}</p>
              </div>
            )}

            {/* Artwork Specifications */}
            {media.artwork_specs && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Artwork Specifications</h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="font-mono text-sm text-amber-800">{media.artwork_specs}</p>
                </div>
              </div>
            )}

            {/* Categories */}
            {media.categories && media.categories.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Categories</h2>
                <div className="flex flex-wrap gap-2">
                  {media.categories.map((cat, idx) => (
                    <span key={idx} className="px-4 py-2 bg-gray-100 text-[#1a1a1a] rounded-full text-sm font-manrope">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Packages & Booking */}
          <div className="space-y-6">
            {/* Ad Placements */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-6 sticky top-6">
              <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Ad Placements</h2>
              
              {media.packages && media.packages.length > 0 ? (
                <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
                  {media.packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg)}
                      data-testid={`package-${pkg.id}`}
                      className={`w-full p-4 border rounded-xl text-left transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-[#D4AF37] bg-[#fee2e2]/30 ring-1 ring-[#D4AF37]'
                          : 'border-[#e5e5e5] hover:border-[#D4AF37]'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-[#1a1a1a]">{pkg.name}</p>
                          <p className="text-xs text-[#737373]">
                            {positionLabels[pkg.position] || pkg.position} • {sizeLabels[pkg.size] || pkg.size}
                          </p>
                        </div>
                        <p className="font-mono font-bold text-lg text-[#D4AF37]">
                          ₹{pkg.price?.toLocaleString('en-IN')} <span className="text-xs font-normal">+5% GST</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#737373]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {pkg.turnaround_days} days
                        </span>
                        {pkg.color && <span>Color</span>}
                        {pkg.bleed && <span>With Bleed</span>}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[#737373] text-sm mb-6">No placements available. Contact us for custom options.</p>
              )}

              {/* Selected Package Summary */}
              {selectedPackage && (
                <div className="border-t border-[#e5e5e5] pt-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#737373]">Selected:</span>
                    <span className="font-semibold text-sm">{selectedPackage.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#737373]">Base Price:</span>
                    <span className="font-mono text-2xl font-bold text-[#D4AF37]">
                      ₹{selectedPackage.price?.toLocaleString('en-IN')} <span className="text-xs font-normal">+5% GST</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Buy Button */}
              <button
                onClick={handleBuyNow}
                disabled={!selectedPackage}
                data-testid="buy-now-btn"
                className="w-full py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-xl flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Book Placement
              </button>

              {/* What's Included */}
              <div className="mt-6 pt-4 border-t border-[#e5e5e5]">
                <p className="text-xs text-[#737373] uppercase tracking-widest mb-3">What's Included:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Ad design assistance
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Print-ready file preparation
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    High-res scan of placement
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Circulation certificate
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrintMediaDetail;
